'use client';

import type { RefObject } from 'react';
import { useCallback, useRef } from 'react';
import type { OzonetelCallStatus } from '../../consts/ozonetel/sdk';
import {
  type AgentAudioState,
  createAgentAudioState,
  createOzonetelGetUserMediaWrapper,
  type GetUserMediaPatchState,
  installGetUserMediaWrapper as installGetUserMediaPatch,
  OZONETEL_DEBUG_PREFIX,
  releaseAgentAudioState,
  restoreGetUserMediaWrapper,
} from '../../utils/ozonetel/agentAudio';

interface UseOzonetelAgentAudioControlParams {
  callStatusRef: RefObject<OzonetelCallStatus>;
}

export function useOzonetelAgentAudioControl({
  callStatusRef,
}: UseOzonetelAgentAudioControlParams) {
  const audioStateRef = useRef<AgentAudioState | undefined>(undefined);
  const isAgentMutedRef = useRef(false);
  const getUserMediaPatchRef = useRef<GetUserMediaPatchState | undefined>(undefined);

  const restoreGetUserMedia = useCallback(() => {
    const patchState = getUserMediaPatchRef.current;
    if (!patchState) return;

    restoreGetUserMediaWrapper(patchState);
    getUserMediaPatchRef.current = undefined;
  }, []);

  const releaseAgentAudio = useCallback(() => {
    restoreGetUserMedia();

    const audioState = audioStateRef.current;
    if (!audioState) {
      isAgentMutedRef.current = false;
      return;
    }

    releaseAgentAudioState(audioState);
    audioStateRef.current = undefined;
    isAgentMutedRef.current = false;
  }, [restoreGetUserMedia]);

  const installGetUserMediaWrapper = useCallback(() => {
    if (!navigator.mediaDevices?.getUserMedia || getUserMediaPatchRef.current) return;

    const mediaDevices = navigator.mediaDevices;
    const nativeGetUserMedia = mediaDevices.getUserMedia;

    const wrappedGetUserMedia = createOzonetelGetUserMediaWrapper({
      getAudioState: () => audioStateRef.current,
      mediaDevices,
      nativeGetUserMedia,
      restoreGetUserMedia,
    });

    getUserMediaPatchRef.current =
      installGetUserMediaPatch({
        mediaDevices,
        wrappedGetUserMedia,
      }) ?? undefined;
  }, [restoreGetUserMedia]);

  const prepareAgentAudioForOutboundCall = useCallback(async () => {
    releaseAgentAudio();

    if (typeof window === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      console.debug(`${OZONETEL_DEBUG_PREFIX} Browser microphone API is not available`);
      return;
    }

    let rawStream: MediaStream | undefined;

    try {
      rawStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      audioStateRef.current = createAgentAudioState(rawStream);

      installGetUserMediaWrapper();
    } catch (error) {
      rawStream?.getTracks().forEach((track) => {
        track.stop();
      });
      console.debug(
        `${OZONETEL_DEBUG_PREFIX} Failed to prepare controlled agent audio stream`,
        error,
      );
    }
  }, [installGetUserMediaWrapper, releaseAgentAudio]);

  const handleMuteAgent = useCallback(() => {
    if (callStatusRef.current !== 'IN_CALL') {
      console.debug(
        `${OZONETEL_DEBUG_PREFIX} Agent mute ignored because call status is ${callStatusRef.current}`,
      );
      return;
    }

    const audioState = audioStateRef.current;
    if (!audioState?.hasCapturedSdkStream) {
      console.debug(
        `${OZONETEL_DEBUG_PREFIX} Agent mute ignored because SDK microphone stream is not available`,
      );
      return;
    }

    if (isAgentMutedRef.current) {
      console.debug(`${OZONETEL_DEBUG_PREFIX} Agent mute ignored because agent is already muted`);
      return;
    }

    audioState.gainNode.gain.value = 0;
    isAgentMutedRef.current = true;
  }, [callStatusRef]);

  const handleUnmuteAgent = useCallback(() => {
    if (callStatusRef.current !== 'IN_CALL') {
      console.debug(
        `${OZONETEL_DEBUG_PREFIX} Agent unmute ignored because call status is ${callStatusRef.current}`,
      );
      return;
    }

    if (!isAgentMutedRef.current) {
      console.debug(`${OZONETEL_DEBUG_PREFIX} Agent unmute ignored because agent is not muted`);
      return;
    }

    const audioState = audioStateRef.current;
    if (!audioState?.hasCapturedSdkStream) {
      console.debug(
        `${OZONETEL_DEBUG_PREFIX} Agent unmute ignored because SDK microphone stream is not available`,
      );
      return;
    }

    audioState.gainNode.gain.value = 1;
    isAgentMutedRef.current = false;
  }, [callStatusRef]);

  return {
    handleMuteAgent,
    handleUnmuteAgent,
    prepareAgentAudioForOutboundCall,
    releaseAgentAudio,
  } as const;
}
